import { Stack } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AppLoader from "../../../../components/AppLoader";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import AddEditAttributeValuesDialog from "../../../../components/Dialog/AddEditAttributeValuesDialog";
import NavigationDrawer from "../../../../components/NavigationDrawer";
import GlobalTable from "../../../../components/Tables/GlobalTable";
import useDebounce from "../../../../hooks/useDebounce";
import { getSingleAttribute } from "../../../../services/attributeFn";
import { deleteAttributeValue, getAllAttributeValuesByAttributeId } from "../../../../services/attributeValueFn";
import { handleOpenSnackBar } from "../../../../utils";
import { deleteConfirmAlert } from "../../../../components/Alert";
import moment from "moment";
import { APP_ICONS } from "../../../../assets";
import { CSVLink } from "react-csv";

const headerData = ["createdAt", "serialNumber", "name", "actions"];

const tableRows = [
    "created_date",
    "sortOrder",
    "attribute_value_name",
    "actions",
];

export function AttributeValue() {
    const { attributeId } = useParams();
    const navigate = useNavigate();
    const openDialog = useRef(null);
    const { t } = useTranslation(["catalogue", "common"]);
    const [isLoading, setIsLoading] = useState(false);
    const [allAttributeValues, setAllAttributeValues] = useState([]);
    const [attribute, setAttribute] = useState(null);
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const debouncedSearchTerm = useDebounce(searchKeys.attributeName, 1000);
    const { currentLanguage } = useSelector((state) => state.languageReducer);

    const fetchAllAttributeValues = async () => {
        try {
            const params = {
                search: debouncedSearchTerm,
            };
            setIsLoading(true);
            const response = await getAllAttributeValuesByAttributeId(
                attributeId,
                params,
            );
            if (response.data.status === "success") {
                let data = response?.data.data.reverse();
                setAllAttributeValues(data);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAttributeById = async () => {
        try {
            const response = await getSingleAttribute(attributeId);
            if (response.data.status === "success") {
                setAttribute(response.data.data);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        }
    };

    useEffect(() => {
        fetchAllAttributeValues();
    }, [debouncedSearchTerm]);

    useEffect(() => {
        fetchAttributeById();
    }, []);

    const prepareExportData = () => {
        return allAttributeValues.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "N/A",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "N/A",
            "Name In Romanian": item.label?.ro || "N/A",
            "Name In English": item.label?.en || "N/A",
            "Value": item.value || "N/A",
            "Serial Number": item.sortOrder || "N/A",
        }));
    };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"contained"}
                    btnText={"addNew"}
                    handleClickBtn={() => {
                        openDialog.current.openDialog({
                            type: "add",
                            data: null,
                            getData: fetchAllAttributeValues,
                        });
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />
                <CSVLink
                    data={prepareExportData()}
                    filename={"attribute_value_list.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={t("export", { ns: "common" })}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>          
            </Stack>
        );
    };

    const handleEdit = (row) => {
        openDialog.current.openDialog({
            type: "edit",
            data: row?.row,
            getData: fetchAllAttributeValues,
        });
    };

    const handleDelete = (row) => {
        deleteConfirmAlert({
            title: t("deleteConfirmation", { ns: "common" }),
            text: t("deleteConfirmationText", { ns: "common" }),
            confirmButtonText: t("delete", { ns: "common" }),
            cancelButtonText: t("cancel", { ns: "common" }),
        }).then(async (result) => {
            if (result) {
                try {
                    const response = await deleteAttributeValue(row?.row?._id);
                    if (response.data.status === "success") {
                        fetchAllAttributeValues();
                        handleOpenSnackBar("success", response.data.message);
                    } else {
                        handleOpenSnackBar("error", response.data.message);
                    }
                } catch (error) {
                    handleOpenSnackBar("error", error.message);
                }
            }
        });
    }
    const renderTable = useMemo(() => {
        return (
            <GlobalTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allAttributeValues || []}
                showActions={true}
                showEdit={true}
                showDelete={true}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
            />
        );
    }, [allAttributeValues]);

    return (
        <NavigationDrawer>
            <AppLoader isVisible={isLoading} />
            <PaperContainer
                title={`${t("attributeValueList")} - ${attribute?.label[currentLanguage]}`}
                subtitle={t("attributeValueDescription")}
                headerActions={renderHeaderAction()}
                showBackButton={true}
                onBack={() => navigate(-1)}
            >
                {renderTable}
            </PaperContainer>
            <AddEditAttributeValuesDialog ref={openDialog} attribute={attribute} />
        </NavigationDrawer>
    );
}
